<template>
  <div
    class="flex flex-col quiz-generator"
    :class="{
      'expand': isShowQuizGeneratorContent
    }"
  >
    <div
      class="quiz-header"
      :class="{
        'expand': isShowQuizGeneratorContent
      }"
    >
      <div class="quiz-header-inner">
        <div
          class="items-center text-base font-bold back-search"
          @click.stop="backToChoiceType"
        >
          <span v-show="isShowQuizGeneratorContent" class="mr-3">
            <i class="fas fa-chevron-left"></i>
          </span>
          <img
            v-show="!isShowQuizGeneratorContent"
            class="inline-block mr-1"
            src="@/assets/images/icon/sparkles_2728.png"
            alt=""
            width="20"
            height="20"
          >
          <span>AI Quiz Generator</span>
        </div>

        <aha-textarea
          class="relative w-full z-1"
          id="quiz-generator-input"
          :placeholder="quizPlaceholder"
          v-model="prompt"
          :maxLength="500"
          @focus="focusInputSearch"
        ></aha-textarea>

        <div class="relative flex items-center justify-between mt-4 z-1">
          <span
            class="font-bold underline cursor-pointer text-base-0"
            @click="showQuizExample"
          >Example</span>
          <aha-button
            variant="base"
            class="px-5"
            :disabled="isSubmitting"
            @click="handleSendMessageToOpenai()"
          >
            Generate
          </aha-button>
        </div>
      </div>
    </div>

    <div
      v-if="isShowQuizGeneratorContent"
      class="search-result"
    >
      <div
        v-if="isSubmitting"
        class="flex content-center justify-center w-full h-full text-center"
      >
        <div class="mt-9">
          <div class="text-base-100">
            Our robot's on it 🤖 Don't go anywhere!
          </div>
          <FakeProgressbar />
        </div>
      </div>
      <div v-else class="search-result-inner">
        <div v-if="chatResponseError" class="text-center mt-15">
          <img class="inline-block mb-4" src="@/assets/images/ai-no-result.svg">
          <p>Looks like our robot's stumped!</p>
          <p>Try rephrasing or providing more details.</p>
        </div>
        <div class="quiz-list" v-else-if="!isSubmitting && chatResponse">
          <div v-for="(response, index) in chatResponse" :key="index" class="quiz-item">
            <div class="flex justify-between pb-4 flex-nowrap">
              <div class="font-bold">
                {{ index+1 }}. {{ response.questionTitle }}
              </div>
              <div class="flex-shrink-0 pl-7">
                <aha-button
                  @click="handleAddSlideToPresentation(response)"
                  :submitting="isAddingSlide"
                  :disabled="isAddingSlide"
                >
                  Add
                </aha-button>
              </div>
            </div>
            <ul class="quiz-item-options">
              <li
                v-for="(options, optIndex) in response.slideOptions"
                :key="optIndex"
                class="quiz-item-option"
                :class="{
                  'marked': options.correct
                }"
              >
                {{ options.title }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <aha-modal
      v-if="isShowModalLimit"
      id="modal-request-limit"
      :title="modalLimitInfo.title"
      @hide="handleModalConfirm(false)"
    >
      <div class="text-base-90">
        {{ modalLimitInfo.body }}
      </div>

      <template #action>
        <aha-link
          v-if="modalLimitInfo.isReachedMaximum"
          @click="handleModalConfirm(false)"
        >
          <span class="font-bold">No, thanks</span>
        </aha-link>
        <aha-button
          v-if="modalLimitInfo.isReachedMaximum"
          variant="primary"
          has-margin-left
          @click="handleModalConfirm(true)"
        >
          Get updates
        </aha-button>
        <aha-button
          v-if="modalLimitInfo.isReachedThreshold"
          variant="primary"
          has-margin-left
          @click="handleModalConfirm(false)"
        >
          Okay
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import mixpanelUtils from '@/utils/mixpanel.utils'
import gaCategories from '@/constant/gaCategories'

import utils from '@/util'
import OpenaiApi from '@/api/openai'
import SlideApi from '@/api/slide'
import EventBus from '@/eventBus'
import quizGenerator from '@/utils/quizGenerator'
import FakeProgressbar from './FakeProgressbar.vue'
import DefaultQuizGenerator from '@/constant/DefaultQuizGenerator'

const REQUEST_MAXIMUM = 150
const REQUEST_THRESHOLD = 140

export default {
  components: {
    FakeProgressbar,
  },
  props: {
    isShowQuizGeneratorContent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      prompt: '',
      chatResponse: '',
      chatResponseError: false,
      isSubmitting: false,
      isAddingSlide: false,
      isShowModalLimit: false,
      quizPlaceholder: quizGenerator.randomQuizPlaceholder(),
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      isNewPresentation: (state) => state.isNewPresentation,
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
      currentUser: (state) => state.currentUser,
    }),
    modalLimitInfo() {
      // slideGeneratorRequestCount >= REQUEST_MAXIMUM
      let title = "You've reached the beta access limit."
      let body = 'Thanks for using the beta version of the AI Quiz Generator! Get updates about the full release of this feature by clicking the button below.'
      const requestCount = (this.currentUser && this.currentUser.slideGeneratorRequestCount) || -1

      if (requestCount === REQUEST_THRESHOLD) {
        title = 'You have 10 uses left.'
        body = 'This beta version is limited to 150 uses. Use them wisely!'
      }

      return {
        isReachedThreshold: requestCount === REQUEST_THRESHOLD,
        isReachedMaximum: requestCount >= REQUEST_MAXIMUM,
        title,
        body,
      }
    },
  },
  mounted() {
    EventBus.$on('slide-search-popup:open', this.backToChoiceType)
    EventBus.$on('quiz-generator:unlock-add-slide', () => {
      this.isAddingSlide = false
    })
  },
  destroyed() {
    EventBus.$off('quiz-generator:unlock-add-slide')
  },
  methods: {
    ...mapActions(['setSlideActiveAction', 'setCurrentUserAction']),

    backToChoiceType() {
      this.prompt = ''
      this.$emit('handleDiveInQuiz', false)
    },

    focusInputSearch() {
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Focus input',
        null,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
    },

    async handleSendMessageToOpenai() {
      if (!this.prompt || !this.prompt.trim().length) return
      const { slideGeneratorRequestCount } = this.$store.state.currentUser

      if (slideGeneratorRequestCount >= REQUEST_MAXIMUM) {
        this.isShowModalLimit = true
        return
      }

      this.isSubmitting = true
      this.$emit('handleDiveInQuiz', true)
      try {
        const res = await OpenaiApi.generateQuiz({
          message: this.prompt,
        })
        const parsedResponse = JSON.parse(res.data)
        this._handleSendMessageSuccess(parsedResponse, res.slideGeneratorRequestCount)
      } catch (error) {
        this.chatResponseError = true
        Sentry.captureException(error, {
          extra: {
            inputText: this.prompt,
          },
        })
      } finally {
        this.isSubmitting = false
      }

      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Generate',
        this.prompt,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
    },

    _handleSendMessageSuccess(res, slideGeneratorRequestCount) {
      if (slideGeneratorRequestCount) this._updateUserRequestCount(slideGeneratorRequestCount)

      if (slideGeneratorRequestCount === REQUEST_THRESHOLD
        || slideGeneratorRequestCount === REQUEST_MAXIMUM) {
        this.isShowModalLimit = true
      }

      if (res && res[0] && res[0].questionTitle) {
        this.chatResponseError = false
        this.chatResponse = res
      }

      if (res && res[0] && res[0].error) {
        this.chatResponse = null
        this.chatResponseError = true
      }
    },

    _updateUserRequestCount(slideGeneratorRequestCount) {
      this.setCurrentUserAction({
        ...this.currentUser,
        slideGeneratorRequestCount,
      })
    },

    async handleAddSlideToPresentation(slide) {
      if (this.isAddingSlide) return

      this.isAddingSlide = true

      try {
        const [payload, newSlideIndex, activeIndex] = this._setPayloadCreateGeneratedSlide(slide)
        const res = await SlideApi.createGeneratedSlide(payload)
        this._handleAfterAddSlide(res.body, newSlideIndex, activeIndex)
      } catch (error) {
        utils.showToastError('Cannot add this slide to current presentation. Please try again')
        Sentry.captureException(error)
      } finally {
        this.isAddingSlide = false
      }
      utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Create new slide', 'AI-generated', 'pickAnswer', {
        ...mixpanelUtils.getPresentationContext(this.$store.state.presentation),
        slideId: this.slideActive.id,
      })
    },

    _setPayloadCreateGeneratedSlide(slide) {
      const payload = {
        ...slide,
        presentationId: this.presentation.id,
        textColour: this.slideActive.textColour || '#313131',
        baseColour: this.slideActive.baseColour || '#ffffff',
        backgroundImage: this.slideActive.backgroundImage,
        visibility: this.slideActive.visibility,
      }
      const activeIndex = this.$store.state.presentation.slides.findIndex((s) => {
        if (s.id) {
          return s.id === this.$store.state.slideActive.id
        }
        return s.timestamp === this.$store.state.slideActive.timestamp
      })
      let newSlideIndex = activeIndex
      if (activeIndex > -1 && !this.isNewPresentation) {
        const isNextSlideLeaderboard = this.presentation.slides[activeIndex + 1] && this.presentation.slides[activeIndex + 1].type === 'leaderboard'
        if (!isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex - 1].order
        } else {
          payload.insertAfter = this.presentation.slides[activeIndex].order + 2
          newSlideIndex = activeIndex + 2
        }
      }
      return [payload, newSlideIndex, activeIndex]
    },

    _handleAfterAddSlide(newSlide, newSlideIndex, activeIndex) {
      const defaultSlideId = this.presentation.slides[activeIndex].id
      this.$store.state.presentation.slides[newSlideIndex] = newSlide
      this.setSlideActiveAction(newSlide)
      setTimeout(() => {
        EventBus.$emit('makeSlideActive', this.presentation.slides[newSlideIndex])
        utils.showToastSuccess('Slide added successfully')
      }, 250)
      if (this.isNewPresentation) {
        this.$store.state.isNewPresentation = false
        // Delete default slide: poll
        if (defaultSlideId) {
          SlideApi.updateSlideById(defaultSlideId, {
            deleted: true,
          })
        }
      }
      EventBus.$emit('addSlideLeaderboard')
    },

    showQuizExample() {
      this.isSubmitting = true
      this.prompt = DefaultQuizGenerator.DEFAULT_PROMPT
      this.$emit('handleDiveInQuiz', true)
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Example',
        null,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
      setTimeout(() => {
        this.chatResponse = DefaultQuizGenerator.DEFAULT_CONTENT
        this.isSubmitting = false
      }, 600)
    },

    async handleModalConfirm(confirm = false) {
      if (confirm) {
        await OpenaiApi.requestMoreQuiz({
          plan: this.$store.state.plan,
        })
        utils.showToastSuccess("Thanks! We'll update you via email.")
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'AI Quiz Generator > Over limit > Send request',
          null,
          null,
          {
            presentationId: this.presentation.id,
          },
        )
      }

      this.isShowModalLimit = false
    },
  },
}
</script>

<style lang="scss" scoped>
.quiz-generator {
  background-color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  &.expand {
    height: 100%;
    background-color: transparent;
  }
}

.quiz-header {
  padding: 16px 20px 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.quiz-header.expand {
  padding: 0;

  .quiz-header-inner {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.quiz-header-inner {
  position: relative;
  background-color: #7564D2;
  border-radius: 8px;
  padding: 20px;
  transition: cubic-bezier(0.075, 0.82, 0.165, 1);
  overflow: hidden;

  &::after {
    z-index: 0;
    position: absolute;
    content: '';
    border-radius: 50%;
    background: linear-gradient(160.79deg, #1D1D54 9.94%, rgba(29, 29, 84, 0) 27.1%);
    opacity: 0.62;
    width: 1176.68px;
    height: 1279px;
    left: -40px;
    top: 52px;
  }
}

.back-search {
  cursor: pointer;
  padding-right: 16px;
  color: #D1D1D1;
}

::v-deep {
  .back-search {
    color: #fff;
  }

  .aha-textarea {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    height: calc(1em * 1.125 * 4 + 1rem);
    line-height: 1.125rem;

    &::placeholder {
      color: #999;
    }
  }
}

.search-result {
  height: 100%;
  padding-bottom: 1rem;
  display: flex;
  background-color: #F9FBFF;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
}

.search-result-inner {
  padding: 0 1rem;
  height: 100%;
  width: 100%;
  overflow-y: auto;
}

.quiz-list {
  .quiz-item {
    @apply shadow rounded-lg p-4 my-4;
    @apply w-full;
    color: #000;
    background-color: #fff;
  }

  .quiz-item-options {
    display: block;
    list-style-type: upper-alpha;
  }

  .quiz-item-option {
    position: relative;
    border-top: 1px solid #ebebeb;
    padding: 1rem 2.25rem 1rem 0;
    list-style-type: upper-alpha;
    list-style-position: inside;

    &::marker {
      font-weight: 700;
    }

    &::after {
      visibility: hidden;
      position: absolute;
      top: 1rem;
      right: 0;
      color: #D3D7E1;
      font-family: 'Font Awesome 5 Free';
      content: '\f058';
      font-weight: 900;
      font-size: 1rem;
    }

    &.marked::after {
      visibility: visible;
    }
  }
}
</style>
